import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Typography,
  Box,
  TypographyProps,
  IconButton,
  Collapse,
} from '@mui/material';
import { useDialog } from 'hooks/common/useDialog';
import type {
  AccordionProps,
  BoxProps,
  CollapseProps,
  IconButtonProps,
} from '@mui/material';
import type { DialogConfig } from 'hooks/common/useDialog';

type SparkAccordionProps = {
  title: React.ReactNode | string;
  icon?: React.ReactNode;
  collapseProps?: CollapseProps;
  accordionTitleStyles?: TypographyProps['sx'];
  accordionTitleProps?: TypographyProps;
  summaryStyles?: BoxProps['sx'];
  detailsStyle?: BoxProps['sx'];
  children: React.ReactNode;
  sx?: AccordionProps['sx'];
  expandedSx?: AccordionProps['sx'];
  primaryIcon?: React.ReactNode;
  secondaryIcon?: React.ReactNode;
  onSecondaryIconClick?: IconButtonProps['onClick'];
  statusIcon?: React.ReactNode;
  initialExpand?: boolean;
  dialogConfig?: DialogConfig;
  onAccordionToggle?: () => void;
  titleContainerProps?: BoxProps['sx'];
  showArrow?: boolean;
};

function SparkAccordion({
  title,
  icon = null,
  collapseProps,
  accordionTitleStyles,
  summaryStyles = {},
  detailsStyle = {},
  children,
  primaryIcon,
  secondaryIcon,
  onSecondaryIconClick,
  statusIcon,
  sx = {},
  showArrow = true,
  dialogConfig: parentDialogControl,
  initialExpand = false,
  accordionTitleProps = {},
  onAccordionToggle,
  titleContainerProps,
  expandedSx = {},
}: SparkAccordionProps) {
  const shouldRenderSecondaryIcon = !!secondaryIcon && onSecondaryIconClick;
  const localDialogControl = useDialog(initialExpand);

  const { open, closeDialog, openDialog } =
    parentDialogControl || localDialogControl;

  const toggleOpen = () => {
    onAccordionToggle && onAccordionToggle();
    if (open) {
      closeDialog();
    } else {
      openDialog();
    }
  };
  const styles = {
    ...sx,
    ...(open ? expandedSx : {}),
  };

  return (
    <Box
      position='relative'
      sx={{
        border: '1px solid transparent',
        borderRadius: '10px',
        width: '100%',
        ...styles,
      }}
    >
      <Box width='100%' borderRadius='10px'>
        <Box
          position='relative'
          paddingY={2}
          paddingX={1}
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          component='div'
          onClick={toggleOpen}
          border='1px solid transparent'
          sx={{ borderRadius: '10px', ...summaryStyles }}
        >
          <Box
            display='flex'
            gap={2}
            alignItems='center'
            sx={titleContainerProps}
          >
            {icon}
            {typeof title === 'string' ? (
              <Typography sx={accordionTitleStyles} {...accordionTitleProps}>
                {title}
              </Typography>
            ) : (
              <>{title}</>
            )}
            {primaryIcon || <></>}
          </Box>
          <Box display='flex' justifyContent='flex-end' width='100%'>
            {shouldRenderSecondaryIcon && (
              <IconButton onClick={onSecondaryIconClick}>
                {secondaryIcon}
              </IconButton>
            )}
            {showArrow && (
              <Box
                sx={{ cursor: 'pointer' }}
                display='flex'
                justifyContent='center'
                alignItems='center'
              >
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
            )}
          </Box>
        </Box>

        <Collapse in={open} unmountOnExit={true} {...collapseProps}>
          <Box width='100%' sx={detailsStyle}>
            {children}
          </Box>
        </Collapse>
      </Box>
      {!!statusIcon && (
        <span style={{ position: 'absolute', top: -10, right: -10 }}>
          {statusIcon}
        </span>
      )}
    </Box>
  );
}

export default SparkAccordion;
